import { groupBy } from 'lodash';
import i18next from 'i18next';

import utilsXLS, { EXPORT_DECIMALS_COUNT } from '@commons/utils/makeXLS';

import {
  ALLERGENS_COLUMN,
  AVAILABILITIES_COLUMNS,
  BRANDS_COLUMN,
  CHANNEL_COMPONENT_COST_COLUMN,
  CHANNEL_COMPONENT_USED_COLUMN,
  CHANNEL_INGREDIENT_COST_COLUMN,
  CHANNEL_INGREDIENT_QUANTITY_COLUMN,
  CHANNEL_MARGIN_COLUMN,
  CHANNEL_RECIPE_COST_COLUMN,
  COMMON_COLUMNS,
  COMPONENT_ALLERGENS_COLUMN,
  COMPONENT_CATEGORY_COLUMN,
  COMPONENT_COMPONENTS_ID_COLUMN,
  COMPONENT_COST_COLUMN,
  COMPONENT_NAME_COLUMN,
  COMPONENT_QUANTITY_COLUMN,
  COMPONENT_TYPE_COLUMN,
  COMPONENT_UNIT_COLUMN,
  DEFAULT_COST_COLUMN,
  INGREDIENT_ALLERGENS_COLUMN,
  INGREDIENT_CATEGORY_COLUMN,
  INGREDIENT_COST_COLUMN,
  INGREDIENT_ID_COLUMN,
  INGREDIENT_NAME_COLUMN,
  INGREDIENT_QUANTITY_COLUMN,
  INGREDIENT_UNIT_COLUMN,
  RECIPE_DEFAULT_COST_COLUMN,
} from './exportRecipesColumns';

import {
  INFO_SHEET_STORE_COLUMN,
  STORE_COLUMN,
  VARIANT_COLUMN,
} from './exportRecipesByStoreColumns';

const applyDecimalsOnNumericColumns = (columns) =>
  columns.map((column) => {
    const isNumericColumn = column.type === 'currency' || column.type === 'percentage';

    if (!isNumericColumn) {
      return column;
    }

    return {
      ...column,
      decimals: EXPORT_DECIMALS_COUNT,
    };
  });

/**
 * Build a lookup map: storeId → Map<recipeId, { combinationKey, topVariantName }>.
 * Used to quickly resolve which composition (and top-level variant name) a store uses per recipe.
 *
 * @param {Array<{ storeId: string, recipeId: string, combinationKey: string, topVariantName: string | null }>} storeResolutions
 * @returns {Map<string, Map<string, { combinationKey: string, topVariantName: string | null }>>}
 */
const buildVariantLookup = (storeResolutions) => {
  const lookup = new Map();

  storeResolutions.forEach(({ storeId, recipeId, combinationKey, topVariantName }) => {
    if (!lookup.has(storeId)) {
      lookup.set(storeId, new Map());
    }

    lookup.get(storeId).set(recipeId, { combinationKey, topVariantName });
  });

  return lookup;
};

const getMainRecipeVariantName = () => i18next.t('ADMIN.RECIPES.MAIN_RECIPE_TAB');
const getAllStoresLabel = () => i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_ALL_STORES');

/**
 * Expand recipe-level rows, grouped by recipe. A recipe with no variant configured on any selected
 * store is emitted as a single row with storeName = "all" (it is identical everywhere). A recipe
 * with variants is expanded per store, picking the variant's row (or the main recipe as fallback).
 *
 * @param {Object[]} mainRecipeRows - One row per recipe (main composition)
 * @param {{ id: string, name: string }[]} selectedStores
 * @param {Map<string, Map<string, { combinationKey: string, topVariantName: string | null }>>} variantLookup
 * @param {Object<string, Object>} recipeRowByKey - Resolved recipe rows keyed by combinationKey
 * @param {Set<string>} recipesWithVariants - Recipe ids resolved to a variant on at least one store
 * @returns {Object[]} Expanded rows
 */
const expandRecipeRowsForStores = (
  mainRecipeRows,
  selectedStores,
  variantLookup,
  recipeRowByKey,
  recipesWithVariants,
) => {
  const expandedRows = [];

  mainRecipeRows.forEach((mainRow) => {
    if (!recipesWithVariants.has(mainRow.id)) {
      expandedRows.push({
        ...mainRow,
        storeName: getAllStoresLabel(),
        variantName: getMainRecipeVariantName(),
      });

      return;
    }

    selectedStores.forEach((store) => {
      const resolution = (variantLookup.get(store.id) ?? new Map()).get(mainRow.id);
      const variantRow = resolution ? recipeRowByKey[resolution.combinationKey] : null;
      const sourceRow = variantRow ?? mainRow;
      const variantName = resolution?.topVariantName ?? getMainRecipeVariantName();

      expandedRows.push({
        ...sourceRow,
        storeName: store.name,
        variantName,
      });
    });
  });

  return expandedRows;
};

/**
 * Expand recipe×component rows, grouped by recipe. A recipe with no variant configured on any
 * selected store is emitted once with storeName = "all". A recipe with variants is expanded per
 * store, picking the variant's component rows (or the main recipe components as fallback).
 *
 * @param {Object[]} mainComponentRows - Recipe×component rows (main composition)
 * @param {{ id: string, name: string }[]} selectedStores
 * @param {Map<string, Map<string, { combinationKey: string, topVariantName: string | null }>>} variantLookup
 * @param {Object<string, Object[]>} componentRowsByKey - Resolved component rows keyed by combinationKey
 * @param {Set<string>} recipesWithVariants - Recipe ids resolved to a variant on at least one store
 * @returns {Object[]} Expanded rows
 */
const expandComponentRowsForStores = (
  mainComponentRows,
  selectedStores,
  variantLookup,
  componentRowsByKey,
  recipesWithVariants,
) => {
  const mainRowsByRecipeId = groupBy(mainComponentRows, 'recipeId');
  const recipeIds = Object.keys(mainRowsByRecipeId);

  const expandedRows = [];

  recipeIds.forEach((recipeId) => {
    if (!recipesWithVariants.has(recipeId)) {
      mainRowsByRecipeId[recipeId].forEach((row) => {
        expandedRows.push({
          ...row,
          storeName: getAllStoresLabel(),
          variantName: getMainRecipeVariantName(),
        });
      });

      return;
    }

    selectedStores.forEach((store) => {
      const resolution = (variantLookup.get(store.id) ?? new Map()).get(recipeId);
      const variantRows = resolution ? componentRowsByKey[resolution.combinationKey] : null;
      const sourceRows = variantRows ?? mainRowsByRecipeId[recipeId];
      const variantName = resolution?.topVariantName ?? getMainRecipeVariantName();

      sourceRows.forEach((row) => {
        expandedRows.push({
          ...row,
          storeName: store.name,
          variantName,
        });
      });
    });
  });

  return expandedRows;
};

// STORE_COLUMN is placed at index 1 (col B), VARIANT_COLUMN at index 3 (col D)
// Col A: ID, Col B: Etablissement, Col C: Name, Col D: Variant, Col E: Status…
const getRecipeCommonColumns = (isMainSheet, clientHasMultipleBrands) => [
  COMMON_COLUMNS.ID(isMainSheet),
  STORE_COLUMN(),
  COMMON_COLUMNS.NAME(),
  VARIANT_COLUMN(),
  COMMON_COLUMNS.STATUS(),
  ...(clientHasMultipleBrands ? [BRANDS_COLUMN()] : []),
  COMMON_COLUMNS.CATEGORY(isMainSheet),
  COMMON_COLUMNS.ASSOCIATED_PRODUCT(),
  COMMON_COLUMNS.QUANTITY(isMainSheet),
  COMMON_COLUMNS.UNIT(isMainSheet),
];

const getMainSheetColumns = (clientChannels, clientHasMultipleBrands) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const channelMarginColumns = clientChannels.map((channel) => CHANNEL_MARGIN_COLUMN(channel));
  const channelCostColumns = clientChannels.map((channel) => CHANNEL_RECIPE_COST_COLUMN(channel));

  const costAndMarginColumns = hasMultipleChannels
    ? [...channelMarginColumns, ...channelCostColumns]
    : [DEFAULT_COST_COLUMN()];

  const formattedCostAndMarginColumns = applyDecimalsOnNumericColumns(costAndMarginColumns);

  return [
    ...getRecipeCommonColumns(true, clientHasMultipleBrands),
    ...formattedCostAndMarginColumns,
    ALLERGENS_COLUMN(),
    ...AVAILABILITIES_COLUMNS(),
  ];
};

const getComponentsSheetColumns = (clientChannels, clientHasMultipleBrands) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const recipeCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_RECIPE_COST_COLUMN(channel))
    : [RECIPE_DEFAULT_COST_COLUMN()];

  const componentUsedColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_COMPONENT_USED_COLUMN(channel))
    : [];

  const componentCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_COMPONENT_COST_COLUMN(channel))
    : [COMPONENT_COST_COLUMN()];

  return [
    ...getRecipeCommonColumns(false, clientHasMultipleBrands),
    ...recipeCostColumns,
    COMPONENT_COMPONENTS_ID_COLUMN(),
    COMPONENT_NAME_COLUMN(),
    COMPONENT_TYPE_COLUMN(),
    COMPONENT_CATEGORY_COLUMN(),
    COMPONENT_QUANTITY_COLUMN(),
    ...componentUsedColumns,
    ...componentCostColumns,
    COMPONENT_UNIT_COLUMN(),
    COMPONENT_ALLERGENS_COLUMN(),
  ];
};

const getIngredientsSheetColumns = (clientChannels, clientHasMultipleBrands) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const recipeCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_RECIPE_COST_COLUMN(channel))
    : [RECIPE_DEFAULT_COST_COLUMN()];

  const ingredientQuantityColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_INGREDIENT_QUANTITY_COLUMN(channel))
    : [INGREDIENT_QUANTITY_COLUMN()];

  const ingredientCostColumns = hasMultipleChannels
    ? clientChannels.map((channel) => CHANNEL_INGREDIENT_COST_COLUMN(channel))
    : [INGREDIENT_COST_COLUMN()];

  return [
    ...getRecipeCommonColumns(false, clientHasMultipleBrands),
    ...recipeCostColumns,
    INGREDIENT_ID_COLUMN(),
    INGREDIENT_NAME_COLUMN(),
    INGREDIENT_CATEGORY_COLUMN(),
    INGREDIENT_UNIT_COLUMN(),
    ...ingredientQuantityColumns,
    INGREDIENT_ALLERGENS_COLUMN(),
    ...ingredientCostColumns,
  ];
};

const aggregateIngredients = (ingredients, clientChannels) => {
  const hasMultipleChannels = clientChannels.length > 1;

  const groupedIngredientsByRecipeId = groupBy(
    ingredients,
    ({ componentId, recipeId, storeName }) => `${componentId}-${recipeId}-${storeName}`,
  );

  const emptyChannelMap = (defaultValue) =>
    Object.fromEntries(clientChannels.map((channel) => [channel.id, defaultValue]));

  const aggregatedIngredients = Object.values(groupedIngredientsByRecipeId).map(
    (ingredientGroup) => {
      if (ingredientGroup.length === 1 || !hasMultipleChannels) {
        return ingredientGroup[0];
      }

      const aggregated = ingredientGroup.reduce(
        (acc, ingredient) => {
          clientChannels.forEach((channel) => {
            if (!ingredient.componentIsUsedByChannelId[channel.id]) {
              return;
            }

            acc.componentIsUsedByChannelId[channel.id] = true;

            acc.componentCostByChannelId[channel.id] +=
              ingredient.componentCostByChannelId[channel.id] || 0;

            acc.componentQuantityByChannelId[channel.id] += ingredient.componentQuantity;
          });

          return acc;
        },
        {
          componentIsUsedByChannelId: emptyChannelMap(false),
          componentCostByChannelId: emptyChannelMap(0),
          componentQuantityByChannelId: emptyChannelMap(0),
        },
      );

      return { ...ingredientGroup[0], ...aggregated };
    },
  );

  return aggregatedIngredients.reduce((acc, ingredient) => {
    if (!ingredient.isIngredient) {
      return acc;
    }

    if (hasMultipleChannels) {
      const ingredientQuantityByChannelId = Object.fromEntries(
        clientChannels.map((channel) => [
          channel.id,
          ingredient.componentIsUsedByChannelId[channel.id]
            ? ingredient.componentQuantityByChannelId?.[channel.id] || ingredient.componentQuantity
            : 0,
        ]),
      );

      const ingredientCostByChannelId = Object.fromEntries(
        clientChannels.map((channel) => [
          channel.id,
          ingredient.componentIsUsedByChannelId[channel.id]
            ? ingredient.componentCostByChannelId[channel.id]
            : 0,
        ]),
      );

      acc.push({ ...ingredient, ingredientQuantityByChannelId, ingredientCostByChannelId });
    } else {
      acc.push(ingredient);
    }

    return acc;
  }, []);
};

/**
 * Generate an Excel export of recipes per store with variant information.
 *
 * @param {Object[]} detailedRecipesList - Formatted recipe list (one entry per recipe)
 * @param {Object[]} detailedRecipesWithComponents - Formatted recipe×component list
 * @param {Object} currency - Currency object
 * @param {Object[]} clientChannels - Client channels
 * @param {boolean} clientHasMultipleBrands - Whether the client has multiple brands
 * @param {{ id: string, name: string }[]} selectedStores - Stores selected for export
 * @param {Array<{ storeId: string, recipeId: string, combinationKey: string, topVariantName: string | null }>} storeResolutions - Per-store resolved composition keys
 * @param {{ recipeRowByKey: Object<string, Object>, componentRowsByKey: Object<string, Object[]> }} variantData - Resolved recipe and component rows keyed by combinationKey
 */
export function exportRecipesByStore(
  detailedRecipesList,
  detailedRecipesWithComponents,
  currency,
  clientChannels,
  clientHasMultipleBrands,
  selectedStores,
  storeResolutions,
  variantData = { recipeRowByKey: {}, componentRowsByKey: {} },
) {
  if (!detailedRecipesList || !selectedStores.length) {
    return;
  }

  const { recipeRowByKey, componentRowsByKey } = variantData;

  const variantLookup = buildVariantLookup(storeResolutions);

  // Recipes resolved to a variant on at least one selected store (top-level or nested sub-recipe);
  // others are identical everywhere and emitted once.
  const recipesWithVariants = new Set(storeResolutions.map(({ recipeId }) => recipeId));

  const expandedRecipes = expandRecipeRowsForStores(
    detailedRecipesList,
    selectedStores,
    variantLookup,
    recipeRowByKey,
    recipesWithVariants,
  );

  const expandedAllComponents = expandComponentRowsForStores(
    detailedRecipesWithComponents,
    selectedStores,
    variantLookup,
    componentRowsByKey,
    recipesWithVariants,
  );

  const expandedComponents = expandedAllComponents.filter(
    ({ isPreparationIngredient }) => !isPreparationIngredient,
  );

  const expandedIngredients = aggregateIngredients(expandedAllComponents, clientChannels);

  const mainSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.RECIPE_PLURAL'),
    getMainSheetColumns(clientChannels, clientHasMultipleBrands),
    expandedRecipes,
    currency,
  );

  const componentsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('ADMIN.RECIPES.COMPONENTS'),
    getComponentsSheetColumns(clientChannels, clientHasMultipleBrands),
    expandedComponents,
    currency,
  );

  const ingredientsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INGREDIENT_PLURAL'),
    getIngredientsSheetColumns(clientChannels, clientHasMultipleBrands),
    expandedIngredients,
    currency,
  );

  const infoSheetRows = selectedStores.map((store) => ({ storeName: store.name }));

  const infoSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INFORMATIONS'),
    [INFO_SHEET_STORE_COLUMN()],
    infoSheetRows,
    currency,
  );

  utilsXLS.makeXLS(i18next.t('ADMIN.RECIPES.EXPORT_BY_STORE_FILENAME'), [
    mainSheet,
    componentsSheet,
    ingredientsSheet,
    infoSheet,
  ]);
}
